import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Search from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import { phappsRequest, phappsBlob } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";

const ACCENT = "#0891b2"; // cyan-600 — matches the "People" admin section

const CARD_SX = {
  p: 2,
  borderRadius: 2,
  border: "1px solid #e5e7eb",
  backgroundColor: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function PlatformBadge({ platform }) {
  if (platform === "android") {
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <AndroidIcon sx={{ fontSize: 16, color: "#3ddc84" }} />
        <Typography variant="body2">Android</Typography>
      </Box>
    );
  }
  if (platform === "ios") {
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <AppleIcon sx={{ fontSize: 16, color: "#555" }} />
        <Typography variant="body2">iOS</Typography>
      </Box>
    );
  }
  return <Typography variant="body2" sx={{ color: "text.disabled" }}>—</Typography>;
}

const AdminMobileLogsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await phappsRequest(
        API_PATHS.ADMIN_MOBILE_LOGS_LIST,
        "GET",
        null,
        { search: search.trim(), page: 1, pageSize: 200 }
      );
      setRows(Array.isArray(res?.rows) ? res.rows : []);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load mobile logs."));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, []); // initial load; search re-runs via the Refresh/Enter handlers

  const handleDownload = useCallback(async (id, fileName) => {
    setDownloadingId(id);
    try {
      const blob = await phappsBlob(API_PATHS.ADMIN_MOBILE_LOGS_DOWNLOAD(id), "GET");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `mobile-log-${id}.log`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to download log file."));
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const gridRows = useMemo(
    () =>
      rows.map((r) => ({
        id: r.MobileLogID,
        uploaded: r.CreatedAt,
        user:
          [r.firstName, r.lastName].filter(Boolean).join(" ").trim() ||
          `User #${r.MobileUserID}`,
        email: r.email || "",
        fileName: r.FileName,
        size: r.FileSize,
        platform: r.Platform,
        appVersion: r.AppVersion,
        note: r.Note,
      })),
    [rows]
  );

  const columns = [
    {
      field: "uploaded",
      headerName: "Uploaded",
      width: 170,
      renderCell: (p) =>
        p.value ? (
          <Typography variant="body2">
            {new Date(p.value).toLocaleString("en-AU", { timeZone: "Australia/Perth" })}
          </Typography>
        ) : (
          "—"
        ),
    },
    {
      field: "user",
      headerName: "Mobile user",
      flex: 1.1,
      minWidth: 170,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{p.value}</Typography>
          {p.row.email && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>{p.row.email}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: "fileName",
      headerName: "File",
      flex: 1.2,
      minWidth: 200,
      renderCell: (p) => (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 18, color: ACCENT, flexShrink: 0 }} />
          <Typography variant="body2" noWrap>{p.value}</Typography>
        </Box>
      ),
    },
    {
      field: "size",
      headerName: "Size",
      width: 100,
      renderCell: (p) => <Typography variant="body2">{formatBytes(p.value)}</Typography>,
    },
    {
      field: "platform",
      headerName: "Platform",
      width: 120,
      renderCell: (p) => <PlatformBadge platform={p.value} />,
    },
    {
      field: "appVersion",
      headerName: "App ver.",
      width: 100,
      renderCell: (p) =>
        p.value ? (
          <Typography variant="body2">{p.value}</Typography>
        ) : (
          <Typography variant="body2" sx={{ color: "text.disabled" }}>—</Typography>
        ),
    },
    {
      field: "note",
      headerName: "Note",
      flex: 1,
      minWidth: 140,
      renderCell: (p) =>
        p.value ? (
          <Tooltip title={p.value}>
            <Typography variant="body2" noWrap>{p.value}</Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ color: "text.disabled" }}>—</Typography>
        ),
    },
    {
      field: "download",
      headerName: "",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Tooltip title="Download log file">
          <span>
            <IconButton
              size="small"
              disabled={downloadingId === p.row.id}
              onClick={() => handleDownload(p.row.id, p.row.fileName)}
              sx={{ color: ACCENT }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-4 w-full max-w-full">
        <Link
          to="/admin"
          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
          ← Admin
        </Link>

        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ color: ACCENT }}>
              Mobile Logs
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Diagnostic log files uploaded from the mobile app · download to inspect
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={load} disabled={loading} sx={{ color: ACCENT }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        )}

        <Box sx={{ ...CARD_SX, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ACCENT, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Uploaded logs ({gridRows.length})
            </Typography>
            <TextField
              size="small"
              placeholder="Search file, name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                ),
              }}
              sx={{ minWidth: 260 }}
            />
          </Box>

          <Box sx={{ height: 640, width: "100%" }}>
            <DataGrid
              rows={gridRows}
              columns={columns}
              loading={loading}
              getRowHeight={() => 56}
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: { sortModel: [{ field: "uploaded", sort: "desc" }] },
              }}
              sx={{
                border: "none",
                height: "100%",
                "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f8fafc" },
                "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, color: "#475569" },
                "& .MuiDataGrid-cell": { borderColor: "#f1f5f9" },
              }}
            />
          </Box>
        </Box>
      </div>
    </div>
  );
};

export default AdminMobileLogsPage;
